import styled from "@emotion/styled";
import MediaCard from "./../../../media-card/MediaCard";
import { KmsTypeComponent } from "@kaltura/mediaspace-shared-types";
import { DragEvent, useRef, useState } from "react";
import DraggableResultsImage from "../search-results/draggable-results-image/DraggableResultsImage";
import { KalturaESearchEntryResult } from "kaltura-typescript-client/api/types/KalturaESearchEntryResult";
import { AccumulatedResults } from "@kaltura/mediaspace-shared-search";

export interface EntrySearchResultsProps<T extends KalturaESearchEntryResult = KalturaESearchEntryResult> {
    /**
     * data to show
     */
    entries: T[];

    /**
     * callback for the "explore" button
     */
    onMore?: (searchResult: T, index: number) => void;

    /**
     * is the current list the result of a search
     */
    isTextSearch?: boolean;

    /**
     * do we have full space or only partial
     */
    isWideLayout: boolean;

    /**
     * (visibly) selected list
     */
    activeEntry?: string;

    /**
     * invalidate media list callback
     */
    onInvalidate?: () => void;

    /**
     * index of the active item in the list
     */
    activeItemIndex: number | null;

    /**
     * boolean flag to indicate the search results items are draggable
     */
    areSearchResultsDraggable?: boolean;

    /**
     * custom media card component passed to EntrySearchResults if needed
     */
    mediaCard?: KmsTypeComponent;

    /**
     * callback to customize drag behavior (e.g., for multi-selection)
     * @param entryId - the ID of the entry being dragged
     * @returns object with IDs to drag and count for drag image
     */
    getDragData?: (entryId: string) => { idsToDrag: string[]; dragItemsCount: number };

    /**
     * Function to update the cached results
     * The oldData and return type use AccumulatedResults structure with entryResults
     */
    updateCachedResults?: (updater: (oldData: AccumulatedResults<T>) => AccumulatedResults<T>) => void;

    /**
     * Callback to register media card refs for focus management
     */
    setMediaCardRef?: (index: number, element: HTMLElement) => void;
}

const StyledUl = styled("ul")({
    padding: 0,
    margin: 0,
});

const StyledEntryResult = styled(
    "li",
    {
        shouldForwardProp: (prop) => prop !== "isDragging",
        // draggable is a prop we do want to forward
    }
)<{ draggable: boolean; isDragging: boolean }>(({ theme, draggable, isDragging }) => ({
    marginBottom: theme.spacing(2),
    display: "block",
    cursor: isDragging ? "grabbing" : draggable ? "grab" : "default",
}));

/**
 * Global Search Entry search results
 */
export function EntrySearchResults<T extends KalturaESearchEntryResult = KalturaESearchEntryResult>(
    props: EntrySearchResultsProps<T>
) {
    const {
        entries,
        activeEntry,
        onMore,
        isWideLayout,
        onInvalidate,
        activeItemIndex,
        areSearchResultsDraggable = false,
        mediaCard,
        getDragData,
        updateCachedResults,
        setMediaCardRef,
    } = props;

    const [isDragging, setIsDragging] = useState(false);
    const dragRefs = useRef<Map<string, HTMLDivElement | null>>(new Map());

    const MediaCardComponent = mediaCard?.component || MediaCard;

    const onDragStart = (e: DragEvent<HTMLLIElement>, entryId: string) => {
        if (!areSearchResultsDraggable) {
            return;
        }

        setIsDragging(true);

        // Use custom drag data if provided, otherwise default to single entry
        const dragData = getDragData?.(entryId) ?? { idsToDrag: [entryId], dragItemsCount: 1 };

        const ref = dragRefs.current.get(entryId);

        if (ref) {
            e.dataTransfer.setDragImage(ref, 50, 50);
            e.dataTransfer.setData(
                "application/json",
                JSON.stringify({
                    type: "entries",
                    ids: dragData.idsToDrag,
                })
            );
        }
    };

    const onDragEnd = () => {
        setIsDragging(false);
    };

    return (
        <StyledUl>
            {entries.map((entry, index) => {
                // Get drag data for this entry (for dynamic drag image count)
                const dragData = getDragData?.(entry.object.id) ?? { idsToD: [entry.object.id], dragItemsCount: 1 };

                return (
                    <StyledEntryResult
                        key={`${entry.object.id}_result`}
                        className="kms-ds-my-media-result-item"
                        draggable={areSearchResultsDraggable}
                        onDragStart={(e) => onDragStart(e, entry.object.id)}
                        isDragging={isDragging}
                        onDragEnd={() => onDragEnd()}
                        ref={(el) => (el ? setMediaCardRef?.(index, el) : undefined)}
                    >
                        <MediaCardComponent
                            media={entry}
                            onMore={() => onMore?.(entry, index)}
                            active={entry.object.id === activeEntry && !isWideLayout}
                            reloadMediaList={onInvalidate}
                            focused={activeItemIndex === index && isWideLayout}
                            isWideLayout={isWideLayout}
                            updateCachedResults={updateCachedResults}
                            {...mediaCard?.props}
                        />
                        {areSearchResultsDraggable && (
                            <DraggableResultsImage
                                key={`${entry.object.id}-drag-${dragData.dragItemsCount}`}
                                ref={(el) => {
                                    dragRefs.current.set(entry.object.id, el);
                                }}
                                imageUrl={entry.object.thumbnailUrl}
                                itemsCount={dragData.dragItemsCount}
                            />
                        )}
                    </StyledEntryResult>
                );
            })}
        </StyledUl>
    );
}
